import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Filter, MoreVertical } from "lucide-react";
import { StatusTrendDataPoint } from "../../types/security";

interface IssueStatusTrendChartProps {
  data: StatusTrendDataPoint[];
  dateRange: "7" | "30" | "90";
  onDateRangeChange: (range: "7" | "30" | "90") => void;
}

interface HoverData {
  day: number;
  date: string;
  opened: number;
  resolved: number;
  inReview: number;
  x: number;
  y: number;
}

export const IssueStatusTrendChart: React.FC<IssueStatusTrendChartProps> = ({
  data,
  dateRange,
  onDateRangeChange,
}) => {
  const [hoverData, setHoverData] = useState<HoverData | null>(null);

  // Calculate max value for scaling, ensuring it's never NaN or Infinity
  const maxValue = useMemo(() => {
    if (!data || data.length === 0) {
      return 1; // Default to 1 to avoid division by zero
    }
    const values = data.flatMap((d) => [
      Number(d.opened) || 0,
      Number(d.resolved) || 0,
      Number(d.inReview) || 0,
    ]);
    const max = Math.max(...values);
    return max > 0 ? max : 1; // Ensure at least 1 to avoid division issues
  }, [data]);

  const chartHeight = 220;
  const chartPadding = 20;

  // Generate axis labels based on date range
  const getAxisLabels = () => {
    const maxDays = parseInt(dateRange);
    if (maxDays <= 7) {
      return data.map((d) => d.date);
    } else if (maxDays <= 30) {
      // Show every 5th day
      return data
        .filter((_, i) => i % 5 === 0 || i === data.length - 1)
        .map((d) => d.date);
    } else {
      // Show every 15th day
      return data
        .filter((_, i) => i % 15 === 0 || i === data.length - 1)
        .map((d) => d.date);
    }
  };

  const axisLabels = getAxisLabels();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-base font-medium">Opened and Resolved Issues</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">Last {dateRange} days</p>
        </div>
        <div className="flex gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <Filter className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Time Range</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDateRangeChange("7")}>
                {dateRange === "7" && "✓ "}Last 7 days
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDateRangeChange("30")}>
                {dateRange === "30" && "✓ "}Last 30 days
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDateRangeChange("90")}>
                {dateRange === "90" && "✓ "}Last 90 days
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => window.location.reload()}>
                Refresh Data
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {/* Legend */}
          <div className="flex items-center justify-end gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-muted-foreground">Opened</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-muted-foreground">Resolved</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-muted-foreground">In Review</span>
            </div>
          </div>

          {/* Chart */}
          <div className="relative" style={{ height: `${chartHeight}px` }}>
            <svg
              className="w-full h-full"
              viewBox={`0 0 900 ${chartHeight}`}
              preserveAspectRatio="none"
            >
              {/* Grid lines */}
              {[0, 25, 50, 75, 100].map((percent) => {
                const y =
                  chartHeight - chartPadding - (percent / 100) * (chartHeight - 2 * chartPadding);
                return (
                  <line
                    key={percent}
                    x1="0"
                    y1={y}
                    x2="900"
                    y2={y}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                );
              })}

              {/* Opened line */}
              <path
                d={data
                  .map((point, idx) => {
                    const x = data.length > 1 ? (idx / (data.length - 1)) * 900 : 0;
                    const openedValue = Number(point.opened) || 0;
                    const y =
                      chartHeight -
                      chartPadding -
                      (openedValue / maxValue) * (chartHeight - 2 * chartPadding);
                    return `${idx === 0 ? "M" : "L"} ${x},${isNaN(y) ? chartHeight - chartPadding : y}`;
                  })
                  .join(" ")}
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
              />

              {/* Resolved line */}
              <path
                d={data
                  .map((point, idx) => {
                    const x = data.length > 1 ? (idx / (data.length - 1)) * 900 : 0;
                    const resolvedValue = Number(point.resolved) || 0;
                    const y =
                      chartHeight -
                      chartPadding -
                      (resolvedValue / maxValue) * (chartHeight - 2 * chartPadding);
                    return `${idx === 0 ? "M" : "L"} ${x},${isNaN(y) ? chartHeight - chartPadding : y}`;
                  })
                  .join(" ")}
                fill="none"
                stroke="#22c55e"
                strokeWidth="2"
              />

              {/* In Review line */}
              <path
                d={data
                  .map((point, idx) => {
                    const x = data.length > 1 ? (idx / (data.length - 1)) * 900 : 0;
                    const inReviewValue = Number(point.inReview) || 0;
                    const y =
                      chartHeight -
                      chartPadding -
                      (inReviewValue / maxValue) * (chartHeight - 2 * chartPadding);
                    return `${idx === 0 ? "M" : "L"} ${x},${isNaN(y) ? chartHeight - chartPadding : y}`;
                  })
                  .join(" ")}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
              />

              {/* Interactive points */}
              {data.map((point, idx) => {
                const x = data.length > 1 ? (idx / (data.length - 1)) * 900 : 0;
                const openedValue = Number(point.opened) || 0;
                const resolvedValue = Number(point.resolved) || 0;
                const inReviewValue = Number(point.inReview) || 0;

                const yOpened =
                  chartHeight -
                  chartPadding -
                  (openedValue / maxValue) * (chartHeight - 2 * chartPadding);
                const yResolved =
                  chartHeight -
                  chartPadding -
                  (resolvedValue / maxValue) * (chartHeight - 2 * chartPadding);
                const yReview =
                  chartHeight -
                  chartPadding -
                  (inReviewValue / maxValue) * (chartHeight - 2 * chartPadding);

                // Ensure no NaN values
                const safeYOpened = isNaN(yOpened) ? chartHeight - chartPadding : yOpened;
                const safeYResolved = isNaN(yResolved) ? chartHeight - chartPadding : yResolved;
                const safeYReview = isNaN(yReview) ? chartHeight - chartPadding : yReview;

                const handleMouseEnter = (e: React.MouseEvent<SVGCircleElement>) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setHoverData({
                    day: point.day,
                    date: point.date,
                    opened: point.opened,
                    resolved: point.resolved,
                    inReview: point.inReview,
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                };

                return (
                  <g key={idx}>
                    {/* Opened point - interactive area */}
                    <circle
                      cx={x}
                      cy={safeYOpened}
                      r="20"
                      fill="transparent"
                      className="cursor-pointer"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={() => setHoverData(null)}
                    />
                    <circle
                      cx={x}
                      cy={safeYOpened}
                      r="2.5"
                      fill="#ef4444"
                      className="pointer-events-none"
                    />

                    {/* Resolved point - interactive area */}
                    <circle
                      cx={x}
                      cy={safeYResolved}
                      r="20"
                      fill="transparent"
                      className="cursor-pointer"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={() => setHoverData(null)}
                    />
                    <circle
                      cx={x}
                      cy={safeYResolved}
                      r="2.5"
                      fill="#22c55e"
                      className="pointer-events-none"
                    />

                    {/* In Review point - interactive area */}
                    <circle
                      cx={x}
                      cy={safeYReview}
                      r="20"
                      fill="transparent"
                      className="cursor-pointer"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={() => setHoverData(null)}
                    />
                    <circle
                      cx={x}
                      cy={safeYReview}
                      r="2.5"
                      fill="#3b82f6"
                      className="pointer-events-none"
                    />
                  </g>
                );
              })}
            </svg>

            {/* Hover tooltip */}
            {hoverData && (
              <div
                className="fixed z-50 bg-white px-3 py-2 rounded-md shadow-xl border text-xs"
                style={{
                  left: `${hoverData.x}px`,
                  top: `${hoverData.y - 10}px`,
                  transform: "translate(-50%, -100%)",
                }}
              >
                <div className="font-semibold mb-1.5">{hoverData.date}</div>
                <div className="space-y-0.5">
                  <div className="flex items-center justify-between gap-6">
                    <span className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      Opened:
                    </span>
                    <span className="font-bold">{hoverData.opened}</span>
                  </div>
                  <div className="flex items-center justify-between gap-6">
                    <span className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Resolved:
                    </span>
                    <span className="font-bold">{hoverData.resolved}</span>
                  </div>
                  <div className="flex items-center justify-between gap-6">
                    <span className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      In Review:
                    </span>
                    <span className="font-bold">{hoverData.inReview}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* X-axis labels */}
          <div className="flex justify-between text-xs text-muted-foreground px-1">
            {axisLabels.map((label, idx) => (
              <span key={idx}>{label}</span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
